import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { listEdges } from "../../api/edgeApi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

// Global edge search, shown in the top bar of every page. The server does the work
// (GET /edges/list?q=): name, edge id, serial, MACs, customer name and location name.
export const MIN_CHARS = 2;
export const RESULT_LIMIT = 8;
const DEBOUNCE_MS = 250;

/** Where "see all results" goes: the Edge Explorer with the query pre-filled. */
export const allResultsPath = (text) => `/edges?q=${encodeURIComponent(text.trim())}`;

/** Second line of a result row: edge id, customer, location, serial (only what exists). */
export function resultSubtitle(e) {
  return [e.edgeId, e.tenant_name, e.locationName, e.serial_number].filter(Boolean).join(" · ");
}

const box = {
  position: "relative",
  maxWidth: 560,
  width: "100%",
};
const inputStyle = {
  width: "100%",
  padding: "9px 64px 9px 12px",
  border: "1px solid var(--border-strong, #cbd5e1)",
  borderRadius: 8,
  background: "var(--bg-card, #fff)",
  color: "var(--text-primary, #0f172a)",
  fontSize: 14,
  outline: "none",
};
const panel = {
  position: "absolute",
  top: "calc(100% + 6px)",
  left: 0,
  right: 0,
  background: "var(--bg-card, #fff)",
  border: "1px solid var(--border, #e2e8f0)",
  borderRadius: 8,
  boxShadow: "var(--shadow-md, 0 4px 12px rgba(15,39,68,.12))",
  overflow: "hidden",
  zIndex: 600,
};

// The search lives in the bar above the edge list only; on every other screen it is just noise.
export const showsEdgeSearch = (pathname) => pathname === "/edges" || pathname === "/edges/";

export default function GlobalEdgeSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const accessToken = useSelector((s) => s.session?.accessToken);
  const [text, setText] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const reqId = useRef(0);

  const query = text.trim();
  const searchable = query.length >= MIN_CHARS;

  // debounced server search; a slow older response never overwrites a newer one
  useEffect(() => {
    if (!searchable || !accessToken) {
      reqId.current += 1;
      setResults([]);
      setTotal(0);
      setLoading(false);
      setError(false);
      return undefined;
    }
    const id = ++reqId.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { items, total: count } = await listEdges(accessToken, { q: query, limit: RESULT_LIMIT, offset: 0 });
        if (id !== reqId.current) return;
        setResults(items);
        setTotal(count);
        setError(false);
        setActive(-1);
      } catch (_e) {
        if (id !== reqId.current) return;
        setResults([]);
        setTotal(0);
        setError(true);
        toast.error(i18n.t("edgeDetail.search.failed"));
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, searchable, accessToken]);

  // Cmd/Ctrl+K focuses the box from anywhere
  useEffect(() => {
    const onKey = (ev) => {
      if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === "k") {
        ev.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // click outside closes the list
  useEffect(() => {
    const onDown = (ev) => {
      if (boxRef.current && !boxRef.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setActive(-1);
  }, []);

  const openEdge = useCallback(
    (edge) => {
      close();
      setText("");
      navigate(`/edges/${edge.id}`);
    },
    [navigate, close]
  );

  const seeAll = useCallback(() => {
    close();
    navigate(allResultsPath(text));
  }, [navigate, close, text]);

  const onKeyDown = (ev) => {
    if (ev.key === "ArrowDown") {
      ev.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(results.length - 1, i + 1));
    } else if (ev.key === "ArrowUp") {
      ev.preventDefault();
      setActive((i) => Math.max(-1, i - 1));
    } else if (ev.key === "Enter") {
      ev.preventDefault();
      if (!searchable) return;
      if (active >= 0 && results[active]) openEdge(results[active]);
      else if (results.length === 1) openEdge(results[0]);
      else seeAll();
    } else if (ev.key === "Escape") {
      close();
      inputRef.current?.blur();
    }
  };

  const showPanel = open && searchable;

  return (
    <div style={box} ref={boxRef}>
      <input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-expanded={showPanel}
        aria-controls="global-edge-search-list"
        aria-label={t("edgeDetail.search.aria")}
        placeholder={t("edgeDetail.search.placeholder")}
        value={text}
        style={inputStyle}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      <span style={{ position: "absolute", right: 12, top: 9, fontSize: 12, color: "var(--text-muted, #64748b)", pointerEvents: "none" }}>
        {typeof navigator !== "undefined" && /Mac/i.test(navigator.platform || "") ? "⌘K" : "Ctrl K"}
      </span>
      {showPanel && (
        <div id="global-edge-search-list" role="listbox" style={panel}>
          {loading && results.length === 0 && <div style={{ padding: 12, fontSize: 13, color: "var(--text-muted, #64748b)" }}>{t("edgeDetail.search.searching")}</div>}
          {!loading && !error && results.length === 0 && (
            <div style={{ padding: 12, fontSize: 13, color: "var(--text-muted, #64748b)" }}>{t("edgeDetail.search.noMatch", { q: query })}</div>
          )}
          {results.map((e, i) => (
            <div
              key={e.id}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(ev) => ev.preventDefault()}
              onClick={() => openEdge(e)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 12px",
                cursor: "pointer",
                background: i === active ? "var(--bg-popup-hover, #f0f4f8)" : "transparent",
                borderTop: i === 0 ? "none" : "1px solid var(--border-subtle, #edf2f7)",
              }}
            >
              <span
                title={e.is_online ? t("edgeDetail.detail.online") : t("edgeDetail.detail.offline")}
                style={{ width: 9, height: 9, borderRadius: "50%", flex: "none", background: e.is_online ? "var(--success, #276749)" : "#94a3b8" }}
              />
              <span style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary, #0f172a)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {e.name || e.edgeId}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-muted, #475569)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {resultSubtitle(e)}
                </div>
              </span>
            </div>
          ))}
          {results.length > 0 && total > results.length && (
            <div
              role="option"
              aria-selected={false}
              onMouseDown={(ev) => ev.preventDefault()}
              onClick={seeAll}
              style={{ padding: "9px 12px", fontSize: 13, cursor: "pointer", color: "var(--info-text, #2c5282)", background: "var(--info-bg, #ebf4ff)", borderTop: "1px solid var(--border, #e2e8f0)" }}
            >
              {t("edgeDetail.search.seeAll", { total })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
